import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { pxToRem, color, ie } from '../../utils'
import { incompatibilityAnalysisActions } from '../../actions'
import { INCOMPATIBILITY_GRID_KEY } from '../../reducers/incompatibilityAnalysis'
import Modal from '../../components/Modal'
import { DownloadButton } from '../../components/Form'
import iconPdf from '../../assets/icons/iconPdf.svg'
import iconExcel from '../../assets/icons/iconExcel.svg'
import IncompatibilityGridTable from './IncompatibilityGridTable'
import IncompatiblePairsList from './IncompatiblePairsList'
import { ink, CHECK_ICON, ALERT_ICON, iconChip } from './gridStyles'
import {
  buildPairIndex,
  indexProducts,
  namesSummary,
  productsWithoutMatrix,
  stalePairs
} from './gridUtils'

// Coluna flex: cabeçalho, avisos, barra e legenda têm altura própria; a matriz fica
// com todo o resto (`flex: 1` + `min-height: 0` no Scroller) e rola dentro do modal,
// que por sua vez não passa da janela.
const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: ${pxToRem(320)};
  padding: ${pxToRem(20)} ${pxToRem(22)} ${pxToRem(22)};
  font-family: 'Open Sans', sans-serif;
  color: ${ink.strong};

  /* nenhum bloco de apoio pode encolher para dar espaço à matriz */
  > *:not([data-grid]) {
    flex: 0 0 auto;
  }
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: ${pxToRem(12)};
`

// Controle segmentado: as duas visões são exclusivas e ficam lado a lado, em vez de
// dois botões soltos que pareciam ações independentes.
const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${color.primary};
  border-radius: ${pxToRem('7px')};
  overflow: hidden;
`

const Segment = styled.button`
  border: 0;
  height: ${pxToRem(34)};
  padding: 0 ${pxToRem(16)};
  font-family: 'Open Sans', sans-serif;
  font-size: ${pxToRem(12)};
  font-weight: bold;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  background: ${({ active }) => (active ? color.primary : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : color.primary)};

  & + & {
    border-left: 1px solid ${color.primary};
  }
`

// Mesmo botão de exportação das outras telas (sólido, com ícone e loader próprio),
// em vez de um outline só desta tela - ver ProductMap/CertificateControl.
const Exports = styled.div`
  display: flex;
  align-items: center;

  > button + button {
    margin-left: ${pxToRem(10)};
  }
`

// Papel do PDF, no mesmo controle segmentado das visões. Fica colado ao botão de PDF
// porque só vale para ele - o Excel não tem folha.
const PaperPicker = styled(Segmented)`
  margin-right: ${pxToRem(8)};
`

const PaperSegment = styled(Segment)`
  height: ${pxToRem(38)};
  padding: 0 ${pxToRem(12)};
`

const PAPER_SIZES = [
  { value: 'A4', hint: 'A4 paisagem: a matriz segue em quantas folhas precisar' },
  { value: 'A0', hint: 'A0 paisagem: a matriz inteira em uma única folha' }
]

const ExportButton = styled(DownloadButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: ${pxToRem(200)};
  height: ${pxToRem(40)};
  margin: 0 0 0 auto;
  padding: 0 ${pxToRem(18)};
  font-size: ${pxToRem(13)};

  img {
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
    margin-right: ${pxToRem(8)};
  }

  span {
    font-weight: bold;
  }
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: ${pxToRem(14)};
  padding-top: ${pxToRem(12)};
  border-top: 1px solid ${ink.line};
  font-size: ${pxToRem(12)};
  color: ${ink.soft};

  > span {
    display: inline-flex;
    align-items: center;
    margin-right: ${pxToRem(18)};
    white-space: nowrap;
  }
`

const OkChip = styled.i`
  ${iconChip(ink.okBg, CHECK_ICON)};
`

const BadChip = styled.i`
  ${iconChip(color.danger, ALERT_ICON)};
`

const Notice = styled.p`
  margin: 0 0 ${pxToRem(10)};
  padding: ${pxToRem(9)} ${pxToRem(12)};
  border: 1px solid ${ink.line};
  border-left: ${pxToRem(3)} solid ${({ tone }) => tone || color.warning};
  border-radius: ${pxToRem('6px')};
  background: ${ink.surface};
  font-size: ${pxToRem(12)};
  line-height: 1.5;
`

const Empty = styled.p`
  text-align: center;
  color: ${ink.soft};
  padding: ${pxToRem(40)} 0;
`

class IncompatibilityGridModal extends Component {
  state = {
    viewOverride: null,
    paperSize: 'A4'
  }

  // O Modal do projeto NUNCA desmonta: quando `open` é false ele apenas troca o
  // conteúdo por um loader (Modal.js:160). Por isso o fetch mora aqui e não em
  // componentDidMount — que rodaria no load da página, sem escopo selecionado.
  componentDidUpdate(prevProps) {
    const { open, scope, fetchGrid } = this.props

    const opened = open && !prevProps.open
    // areaId entra na comparação junto com cabinetId: trocar de área mantendo o mesmo
    // cabinetId (null, no escopo de área) deixaria a matriz da área anterior na tela.
    const scopeChanged =
      open &&
      scope &&
      prevProps.scope &&
      (scope.cabinetId !== prevProps.scope.cabinetId || scope.areaId !== prevProps.scope.areaId)

    if (opened || scopeChanged) {
      this.setState({ viewOverride: null })
      fetchGrid({ areaId: scope.areaId, cabinetId: scope.cabinetId })
    }
  }

  // Sem teto de apresentação: a matriz é oferecida em qualquer escopo que o backend
  // consiga calcular (INCOMPATIBILITY_GRID_MAX_PRODUCTS). O IE11 continua de fora
  // porque não tem position: sticky - sem eixos fixos a matriz é ilegível.
  gridAvailable = products => !ie.isIe11() && products.length > 0

  currentView = products => {
    const { viewOverride } = this.state

    if (!this.gridAvailable(products)) return 'list'

    return viewOverride || 'grid'
  }

  renderNotices(data, products, pairs) {
    const semMatriz = productsWithoutMatrix(products)
    const desatualizados = stalePairs(pairs)

    return (
      <Fragment>
        {data.exceeded_limit && (
          <Notice tone={color.danger}>
            Este escopo tem {data.total_products} produtos, acima do limite de análise de{' '}
            {data.max_products}. A matriz não foi calculada — abra por armário para
            analisar um escopo menor.
          </Notice>
        )}

        {ie.isIe11() && (
          <Notice tone={ink.soft}>
            Visão em matriz indisponível neste navegador. Os pares incompatíveis estão listados
            abaixo.
          </Notice>
        )}

        {data.truncated && (
          <Notice>
            <strong>Resultado incompleto:</strong> foram encontrados mais motivos do que este
            relatório comporta. Analise por armário antes de usar esta matriz como evidência.
          </Notice>
        )}

        {/* só faz sentido quando a matriz foi calculada: o texto fala da ausência de
            vermelho nas linhas, e acima do teto não há linha nenhuma. */}
        {!data.exceeded_limit && semMatriz.length > 0 && (
          <Notice>
            <strong>Atenção:</strong> {semMatriz.length} de {products.length} produtos não
            possuem matriz de incompatibilidade cadastrada — <em>{namesSummary(semMatriz)}</em>.
            Eles não geram conflito a partir de si, e a ausência de vermelho nessas linhas não
            significa compatibilidade verificada.
          </Notice>
        )}

        {desatualizados.length > 0 && (
          <Notice>
            {desatualizados.length} par(es) divergem entre o cadastro atual e o índice usado
            pela validação do sistema. Eles aparecem em amarelo.
          </Notice>
        )}
      </Fragment>
    )
  }

  // `productsById` e `pairIndex` dependem só do payload, mas eram remontados a cada
  // render. Como as duas viram props do IncompatibilityGridTable (PureComponent), a
  // identidade nova forçava o re-render das ~22.500 células.
  derived(data) {
    if (!data) return { productsById: {}, pairIndex: {} }
    if (this.derivedCache && this.derivedCache.data === data) return this.derivedCache

    this.derivedCache = {
      data,
      productsById: indexProducts(data.products),
      pairIndex: buildPairIndex(data.incompatibilities)
    }

    return this.derivedCache
  }

  // Sem este aviso a falha da exportação era silenciosa: o botão só parava de girar.
  renderDownloadError() {
    const { downloadError } = this.props

    if (!downloadError) return null

    return downloadError === 'busy' ? (
      <Notice role="alert">
        Outra exportação da matriz está em andamento no servidor. Tente novamente em
        instantes.
      </Notice>
    ) : (
      <Notice role="alert" tone={color.danger}>
        Não foi possível exportar a matriz. Tente novamente.
      </Notice>
    )
  }

  renderBody() {
    const { data, downloading, downloadingXlsx, downloadPdf, downloadXlsx, scope } = this.props

    if (!data) return null

    const products = data.products
    const pairs = data.incompatibilities
    const { productsById, pairIndex } = this.derived(data)
    const view = this.currentView(products)
    const { paperSize } = this.state
    // Acima do teto o backend devolve os produtos mas NÃO calcula nada
    // (incompatibilities: []). Desenhar a grade aí pintaria de verde 500x500 células
    // como se a análise tivesse rodado - e o PDF e o Excel omitem a matriz nesse caso.
    const analysed = !data.exceeded_limit
    const canToggle = analysed && this.gridAvailable(products)

    return (
      <Content>
        {this.renderNotices(data, products, pairs)}
        {this.renderDownloadError()}

        <Toolbar>
          {canToggle ? (
            <Segmented>
              <Segment
                type="button"
                active={view === 'grid'}
                onClick={() => this.setState({ viewOverride: 'grid' })}
              >
                Matriz
              </Segment>
              <Segment
                type="button"
                active={view === 'list'}
                onClick={() => this.setState({ viewOverride: 'list' })}
              >
                Incompatibilidades
              </Segment>
            </Segmented>
          ) : (
            <span />
          )}

          <Exports>
            <PaperPicker role="group" aria-label="Papel do PDF">
              {PAPER_SIZES.map(paper => (
                <PaperSegment
                  key={paper.value}
                  type="button"
                  title={paper.hint}
                  aria-pressed={paperSize === paper.value}
                  active={paperSize === paper.value}
                  onClick={() => this.setState({ paperSize: paper.value })}
                >
                  {paper.value}
                </PaperSegment>
              ))}
            </PaperPicker>

            <ExportButton loading={downloading} onClick={() => downloadPdf(scope, paperSize)}>
              <img src={iconPdf} alt="" aria-hidden="true" />
              <span>Exportar PDF</span>
            </ExportButton>

            {/* O PDF não comporta escopos grandes: 260 produtos são 260 colunas numa
                folha A4. A planilha traz a mesma matriz, com os eixos congelados. */}
            <ExportButton loading={downloadingXlsx} onClick={() => downloadXlsx(scope)}>
              <img src={iconExcel} alt="" aria-hidden="true" />
              <span>Exportar Excel</span>
            </ExportButton>
          </Exports>
        </Toolbar>

        {!analysed ? (
          <Empty>Matriz não calculada para este escopo.</Empty>
        ) : products.length === 0 ? (
          <Empty>Nenhum produto para analisar neste escopo.</Empty>
        ) : view === 'grid' ? (
          <IncompatibilityGridTable
            products={products}
            productsById={productsById}
            pairIndex={pairIndex}
          />
        ) : (
          <IncompatiblePairsList pairs={pairs} productsById={productsById} />
        )}

        <Legend>
          <span>
            <OkChip />
            compatível
          </span>
          <span>
            <BadChip />
            incompatível
          </span>
        </Legend>
      </Content>
    )
  }

  render() {
    const { open, loading, error, scope, closeGrid } = this.props

    // `fullHeight` fica de fora do IE11: lá o flexbox é pela metade (min-height: 0 não
    // segura a coluna), e o modal continua crescendo com o conteúdo, como sempre fez.
    return (
      <Modal
        title={`Matriz de Incompatibilidade (FL - SSMA - 0059) ${scope && scope.title ? ` — ${scope.title}` : ''}`}
        open={open}
        size="large"
        fullHeight={!ie.isIe11()}
        safeContent={false}
        loading={loading}
        error={error}
        errorMessage={'Falha ao carregar a matriz de incompatibilidade.'}
        closeMethod={closeGrid}
      >
        {this.renderBody()}
      </Modal>
    )
  }
}

const mapStateToProps = state => {
  const grid = state.incompatibilityAnalysis[INCOMPATIBILITY_GRID_KEY]

  return {
    open: grid.open,
    scope: grid.scope,
    data: grid.data,
    // O default de `loading` no Modal é true: omitir a prop travaria o modal no loader.
    loading: grid.loading,
    error: grid.error,
    downloading: grid.downloading,
    downloadingXlsx: grid.downloadingXlsx,
    downloadError: grid.downloadError
  }
}

const mapDispatchToProps = dispatch => ({
  fetchGrid: scope => dispatch(incompatibilityAnalysisActions.fetchIncompatibilityGrid(scope)),
  downloadPdf: (scope, paperSize) =>
    dispatch(incompatibilityAnalysisActions.fetchIncompatibilityGridPdf(scope, paperSize)),
  downloadXlsx: scope =>
    dispatch(incompatibilityAnalysisActions.fetchIncompatibilityGridXlsx(scope)),
  closeGrid: () => dispatch(incompatibilityAnalysisActions.closeIncompatibilityGrid())
})

export default connect(mapStateToProps, mapDispatchToProps)(IncompatibilityGridModal)



// WEBPACK FOOTER //
// ./src/containers/IncompatibilityAnalysis/IncompatibilityGridModal.js